import React, { useCallback, useState, useEffect, useRef } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useDateRangePicker } from "@sunwing/shared-components";
import { addDays, addMonths } from "date-fns";
import { CONFIGS } from "CONFIGS";
import { AppContext } from "../../context";
import { openLinkWithPostMethod } from "../Shared/utils";
import { validatePassengers } from "./utils";
import { TripTypeField } from "./TripTypeField";
import { PillButtonGroup } from "../Shared/PillButtonGroup";
import { FromField } from "./FromField";
import { ToField } from "./ToField";
import { DatesField } from "../Shared/DatesField";
import { PassengersField } from "./PassengersField";
import { ClassField } from "./ClassField/Index";
import { MultipleFlightComponent } from "./MultipleFlight";
import { MultipleFlightTabletComponent } from "./MultipleFlightTablet";
import { Button } from "../../components/Button";
import { BRANDS, MODES, TYPES } from "../../configs";
import {
	FLIGHTS_CLASS_ID,
	FLIGHTS_DATES_FIELD_ID,
	FLIGHTS_FROM_FIELD_ID,
	FLIGHTS_TO_FIELD_ID,
} from "../../configs/const";
import { useInputSearchContext } from "../../context/app/searchValuesContext";
import { getFieldsCompleteChildren } from "./utils/getFieldsCompleteChildren";

import sharedStyles from "../Shared/styles.scss";
import styles from "./Flights.scss";

const maxChildAge = 11;

const Flights = ({ tabs, mode}) => {
	const [useAppState] = AppContext.useAppContext();
	const isStackMobileLayout = !useAppState.isSOVNewMobileDesign;
	const today = new Date();
	const isSOVBrand = useAppState.brand === BRANDS.SOV;
	const isMobile = window.matchMedia("(max-width: 768px)").matches;
	const dictionary = {
		"class-list": [
			{
				value: "Y",
				label: useAppState.getDictionary("economy-class"),
				id: useAppState.getDictionary("economy-class"),
			},
			{
				value: "S",
				label: useAppState.getDictionary("premium-class"),
				id: useAppState.getDictionary("premium-class"),
			},
			{
				value: "C",
				label: useAppState.getDictionary("business-class"),
				id: useAppState.getDictionary("business-class"),
			},
			{
				value: "F",
				label: useAppState.getDictionary("first-class"),
				id: useAppState.getDictionary("first-class"),
			},
		],

		"flight-type": [
			{
				label: useAppState.getDictionary("round-trip-flight"),
				id: "RE",
				value: "roundtrip",
			},
			{
				label: useAppState.getDictionary("oneway-flight"),
				id: "OW",
				value: "oneway",
			},
			{
				label: useAppState.getDictionary("multicity-flight"),
				id: "MC",
				value: "multicity",
			},
		],
	};

	const {
		values: { from: _from },
		storeSearchValues,
	} = useInputSearchContext();

	const ishomePageTabletWidget = tabs?.includes(TYPES.FLIGHTS);

	const [previousSession, setPreviousSession] = useState({});

	const [selectedType, setSelectedType] = useState(dictionary["flight-type"][0].value);
	const [isValidSelectedType, setIsValidSelectedType] = useState(true);

	const [selectedFrom, setSelectedFrom] = useState();
	const [isValidSelectedFrom, setIsValidSelectedFrom] = useState(true);

	const [selectedTo, setSelectedTo] = useState();
	const [isValidSelectedTo, setIsValidSelectedTo] = useState(true);

	const [initialDatesFromSharedComponent, setInitialDatesFromSharedComponent] = useState();
	const [selectedDates, setSelectedDates] = useState();
	const [availableDates, setAvailableDates] = useState();
	const [isValidSelectedDates, setIsValidSelectedDates] = useState(true);

	const [selectedPassengers, setSelectedPassengers] = useState({});
	const [isValidSelectedPassengers, setIsValidSelectedPassengers] = useState(true);

	const [selectedClass, setSelectedClass] = useState(dictionary["class-list"][0]);
	const [isValidSelectedClass, setIsValidSelectedClass] = useState(true);

	const [isValidateMultipleCities, setValidateMultipleCities] = useState([
		{
			from: true,
			to: true,
			date: true,
		},
		{
			from: true,
			to: true,
			date: true,
		},
		{
			from: true,
			to: true,
			date: true,
		}
	]);

	let length = 0;

	if(useAppState.sovTablet) {
		length = 2
	}
	if(useAppState.sovDesktop) {
		length = 1
	}
	const [numberOfMultipleFlightTabletInput, setNumberOfMultipleFlightTabletInput] = useState(length);

	const [multipleFlightData, setMultipleFlightData] = useState({
		flight1: {
			selectedFrom: _from?.value ? _from : {},
			selectedTo: {},
			selectedDate: {},
		},
		flight2: {
			selectedFrom: {},
			selectedTo: {},
			selectedDate: {},
		},
		flight3: {
			selectedFrom: {},
			selectedTo: {},
			selectedDate: {},
		},
	});

	useEffect(() => {
		// Restore previous / defaults
		// Flights search box is not loaded into SV - so we will not have to retrieve data from SV
		(async () => {
			const prevData = await useAppState.SessionManager.getSession("flights");
			setSelectedType(
				prevData?.selectedType ?? (isSOVBrand ? "roundtrip" : "RE")
			);
			setPreviousSession({
				selectedFrom: prevData?.selectedFrom ?? null,
				selectedTo: prevData?.selectedTo ?? null,
				selectedDates: prevData?.selectedDates,
			});
			setSelectedPassengers(
				validatePassengers(prevData?.selectedPassengers).isValid
					? prevData?.selectedPassengers
					: {
							adults: 2,
							children: [],
					  }
			);
			storeSearchValues({
				type: "defaultRoomsAndGuests",
				defaultRoomsAndGuests: validatePassengers(prevData?.selectedPassengers).isValid
					? prevData?.selectedPassengers
					: {
							adults: 2,
							children: [],
					  },
			});

			useAppState.emitter.emit("initialized");
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	|--------------------------------------------------
	| Handle background scroll lock on sov mobile
	|--------------------------------------------------
	*/
	const referenceElement = useRef(null);

	const handleUpdateDefaults = useCallback(
		defaults => {
			try {
				/**
				 * Precedence for previous session over defaults unless it is
				 * undefined / null or is set to force override
				 */
				const mergedDefaults = {
					...previousSession,
					selectedFrom:
						previousSession.selectedFrom && !defaults?.selectedFrom?.force
							? previousSession.selectedFrom
							: defaults?.selectedFrom,
				};
				setPreviousSession(mergedDefaults);
			} catch (err) {
				console.warn("WEB.CDB.BSB | Unable to set default values", err);
			}
		},
		[previousSession]
	);

	/**
	|--------------------------------------------------
	| For an instance where user did not interract with 
	| the calendar widget and want to use the default date.
	| The only time SelectedDates value is initialized is 
	| when calendar widget is opened / interracted with
	|--------------------------------------------------
	*/
	const {
		departureDate,
		returnDate,
	} = useDateRangePicker({
		departureDate: selectedDates?.[0]?.toDate() ?? (isSOVBrand ? addDays(today, 1) : addMonths(today, 1)),
		returnDate: selectedDates?.[1]?.toDate() ?? (isSOVBrand ? addDays(today, 8) : addDays(addMonths(today, 1), 7)),
		handleDateChange: ()=>{},
	});
	
	useEffect(() => {
		setInitialDatesFromSharedComponent([dayjs(departureDate), dayjs(returnDate)]);
		setSelectedDates([dayjs(departureDate), dayjs(returnDate)]);
		
		setMultipleFlightData( prev => ({
		...prev,
		flight1: {
			...prev.flight1,
			selectedDate: dayjs(departureDate),
		},
		flight2: {
			...prev.flight2,
			selectedDate: dayjs(departureDate),
		},
		flight3: {
			...prev.flight3,
			selectedDate: dayjs(departureDate),
		},
	}));
	}, [departureDate, returnDate]);

	const validateSOVSearchCriteria = () => {
		const validSelectedType = !!selectedType;
		setIsValidSelectedType(validSelectedType);

		let validSelectedDates;
		let validSelectedFrom;
		let validSelectedTo;
		let allValidation;

		const validSelectedClass = !!selectedClass;
		setIsValidSelectedClass(validSelectedClass);

		const validSelectedPassengers = validatePassengers(selectedPassengers).isValid;
		setIsValidSelectedPassengers(validSelectedPassengers);
		
		const leg1From = !!multipleFlightData?.flight1?.selectedFrom?.label;
		const leg2From = !!multipleFlightData?.flight1?.selectedTo?.label;
		const leg3From = !!multipleFlightData?.flight3?.selectedFrom?.label;

		const leg1To = !!multipleFlightData?.flight1?.selectedTo?.label;
		const leg2To = !!multipleFlightData?.flight2?.selectedTo?.label;
		const leg3To = !!multipleFlightData?.flight3?.selectedTo?.label;

		if((useAppState.sovDesktop || useAppState.sovTablet || isStackMobileLayout) && (selectedType === "roundtrip" || selectedType === "oneway")) {
			validSelectedFrom = !!selectedFrom?.value;
			setIsValidSelectedFrom(validSelectedFrom);

			validSelectedTo = !!selectedTo?.value;
			setIsValidSelectedTo(validSelectedTo);

			if(selectedDates === undefined) {
				validSelectedDates = initialDatesFromSharedComponent.every(date => date);
				setIsValidSelectedDates(validSelectedDates);
			} else {
				validSelectedDates = selectedDates?.every(date => date);
				setIsValidSelectedDates(validSelectedDates);
			}
			
			allValidation = validSelectedType &&
				validSelectedFrom &&
				validSelectedTo &&
				validSelectedClass &&
				validSelectedDates &&
				validSelectedPassengers
	
		}

		let leg1Date = multipleFlightData?.flight1?.selectedDate
		? selectedDates?.[0]?.isBefore(multipleFlightData.flight1.selectedDate, "day") ||
		selectedDates?.[0]?.isSame(multipleFlightData.flight1.selectedDate, "day")
		: true;
		let leg2Date = true;
		let leg3Date = true;

		if ( useAppState.sovTablet ) {
			leg1Date = multipleFlightData?.flight1?.selectedDate &&
			multipleFlightData?.flight2?.selectedDate &&
			(multipleFlightData.flight1?.selectedDate?.isBefore(multipleFlightData.flight2.selectedDate, "day") ||
			multipleFlightData.flight1.selectedDate.isSame(multipleFlightData.flight2.selectedDate, "day"));
		}
		
		if ( numberOfMultipleFlightTabletInput === 2 ) {
			leg2Date =
			multipleFlightData?.flight1?.selectedDate &&
			multipleFlightData?.flight2?.selectedDate &&
			(multipleFlightData.flight1?.selectedDate?.isBefore(multipleFlightData.flight2.selectedDate, "day") ||
			multipleFlightData.flight1.selectedDate.isSame(multipleFlightData.flight2.selectedDate, "day"));
		}
		
		if ( numberOfMultipleFlightTabletInput === 3 ) {
			
			leg3Date =
			multipleFlightData?.flight2?.selectedDate &&
			multipleFlightData?.flight3?.selectedDate &&
			(multipleFlightData.flight2.selectedDate.isBefore(multipleFlightData.flight3.selectedDate, "day") ||
			multipleFlightData.flight2.selectedDate.isSame(multipleFlightData.flight3.selectedDate, "day"));
		}

		if (useAppState.sovTablet && selectedType === "multicity") {
			const allLegValidation = [];
			if(numberOfMultipleFlightTabletInput === 1) {
				allLegValidation.push({from: leg1From, to: leg1To, date: true})
				allValidation = leg1From && leg1To && leg2From && validSelectedClass && validSelectedPassengers
			}
			else if(numberOfMultipleFlightTabletInput === 2) {
				allLegValidation.push({from: leg1From, to: leg1To, date: true}, {from: leg2From, to: leg2To, date: leg1Date})
				allValidation = leg1From && leg1To && leg2From && leg2To && validSelectedClass && validSelectedPassengers
			}
			else if(numberOfMultipleFlightTabletInput === 3) {
				allLegValidation.push({from: leg1From, to: leg1To, date: true}, {from: leg2From, to: leg2To, date: leg1Date}, {from: leg3From, to: leg3To, date: leg3Date})
				allValidation = leg1From && leg1To && leg2From && leg2To && leg3From && leg3To && validSelectedClass && validSelectedPassengers
			}
			
			setValidateMultipleCities(allLegValidation);
			validSelectedDates = true;
			
		}
		
		if (useAppState.sovDesktop && selectedType === "multicity") {
			validSelectedFrom = !!selectedFrom?.value;
			setIsValidSelectedFrom(validSelectedFrom);

			validSelectedTo = !!selectedTo?.value;
			setIsValidSelectedTo(validSelectedTo);

			const allLegValidation = [];
			if(numberOfMultipleFlightTabletInput === 1) {
				allLegValidation.push({from: leg1From, to: leg1To, date: leg1Date})
				allValidation = validSelectedFrom && validSelectedTo && leg1From && leg1To && leg2From && validSelectedType && validSelectedClass && validSelectedPassengers
			}
			else if(numberOfMultipleFlightTabletInput === 2) {
				allLegValidation.push({from: leg1From, to: leg1To, date: leg1Date}, {from: leg2From, to: leg2To, date: leg2Date})
				allValidation = validSelectedFrom && validSelectedTo && leg1From && leg1To && leg2From && leg2To && validSelectedClass && validSelectedPassengers
			}
			
			setValidateMultipleCities(allLegValidation);
			validSelectedDates = true;
			
		}

		return (allValidation);
	};

	const validateSearchCriteria = () => {
		const validSelectedType = !!selectedType;
		setIsValidSelectedType(validSelectedType);

		const validSelectedFrom = !!selectedFrom;
		setIsValidSelectedFrom(validSelectedFrom);

		const validSelectedTo = !!selectedTo;
		setIsValidSelectedTo(validSelectedTo);

		const validSelectedDates = selectedDates.every(date => date);
		setIsValidSelectedDates(validSelectedDates);

		const validSelectedPassengers = validatePassengers(selectedPassengers).isValid;
		setIsValidSelectedPassengers(validSelectedPassengers);

		return (
			validSelectedType &&
			validSelectedFrom &&
			validSelectedTo &&
			validSelectedDates &&
			validSelectedPassengers
		);
	};

	const handleSearch = async (event, openInNewTab = false) => {
		event.preventDefault();
		
		if (isSOVBrand) {
			if (!validateSOVSearchCriteria()) {
				return;
			}

			// Set up dataLayer tracking if not available
			if (!window.dataLayer) {
				window.dataLayer = [];
			}

			// Track flight leg data
			const trackingArray = [];

			let bookingPathURL = "";

			const { adults, children, infants } = selectedPassengers;

			let childAges = "";
			children.forEach(child => {
				childAges += `&ages[]=${child}`;
			});

			if (infants > 0) {
				for (let infant = 0; infant < infants; infant++) {
					childAges += "&ages[]=0";
				}
			}

			// Create the common departure leg
			const requiredKeys = ["selectedDate", "selectedFrom", "selectedTo"];
			const _multipleFlightData = getFieldsCompleteChildren(multipleFlightData, requiredKeys)

			// Create the common departure leg
			const leg1 = `&leg[]=${selectedFrom?.value};${selectedTo?.value};${dayjs(
				selectedDates?.[0]
			).format("YYYY-MM-DD")}`;

			let childrenPlusInfant = children.length;
			if(infants){
				childrenPlusInfant += infants
			}

			if (selectedType === "roundtrip") {
				const leg2 = `&leg[]=${selectedTo?.value};${selectedFrom?.value};${dayjs(
					selectedDates?.[1]
				).format("YYYY-MM-DD")}`;

				bookingPathURL = `https://flight.selloffvacations.com/selloff/flights/search?adults=${adults}&children=${
					childrenPlusInfant
				}${childAges}&trip=${selectedType}&preferedClass=${selectedClass.value}&viewtype=R&lang=${
					useAppState.lang
				}${leg1}${leg2}`;

				window.dataLayer.push({
					event: "flight_search",
					flight_search_dep_date: selectedDates?.[0].format("YYYY-MM-DD"), // YYYY-MM-DD
					flight_search_dep_month: selectedDates?.[0].format("MMMM"),
					flight_search_duration: selectedDates?.[1].diff(selectedDates?.[0], "day"),
					flight_search_mode: selectedType,
					flight_search_type: selectedClass.value,
					flight_search_from: selectedFrom?.label,
					flight_search_to: selectedTo?.label,
					flight_search_num_adult: selectedPassengers.adults,
					flight_search_num_children:
						selectedPassengers.infants + selectedPassengers.children.length,
					flight_search_num_rooms: selectedPassengers.rooms,
					flight_search_total_pax:
						selectedPassengers.adults +
						selectedPassengers.infants +
						selectedPassengers.children.length,
				});
			} else if (selectedType === "oneway") {
				bookingPathURL = `https://flight.selloffvacations.com/selloff/flights/search?adults=${adults}&children=${
					childrenPlusInfant
				}${childAges}&trip=${selectedType}&preferedClass=${selectedClass.value}&viewtype=R&lang=${
					useAppState.lang
				}${leg1}`;

				window.dataLayer.push({
					event: "flight_search",
					flight_search_dep_date: selectedDates?.[0].format("YYYY-MM-DD"), // YYYY-MM-DD
					flight_search_dep_month: selectedDates?.[0].format("MMMM"),
					flight_search_duration: 0,
					flight_search_mode: selectedType,
					flight_search_type: selectedClass.value,
					flight_search_from: selectedFrom?.label,
					flight_search_to: selectedTo?.label,
					flight_search_num_adult: selectedPassengers.adults,
					flight_search_num_children:
						selectedPassengers.infants + selectedPassengers.children.length,
					flight_search_num_rooms: selectedPassengers.rooms,
					flight_search_total_pax:
						selectedPassengers.adults +
						selectedPassengers.infants +
						selectedPassengers.children.length,
				});
			} else if (selectedType === "multicity") {
				const isFirstDateValid =
					selectedDates?.[0]?.isBefore(multipleFlightData?.flight1?.selectedDate, "day") ||
					selectedDates?.[0]?.isSame(multipleFlightData?.flight1?.selectedDate, "day");

				const isFlight1BeforeFlight2 =
					multipleFlightData?.flight1?.selectedDate?.isBefore(multipleFlightData?.flight2?.selectedDate, "day") ||
					multipleFlightData?.flight1?.selectedDate?.isSame(multipleFlightData?.flight2?.selectedDate, "day");

				// eslint-disable-next-line no-unused-vars
				const isFlight2BeforeFlight3 =
					multipleFlightData?.flight2?.selectedDate?.isBefore(multipleFlightData?.flight3?.selectedDate, "day") ||
					multipleFlightData?.flight2?.selectedDate?.isSame(multipleFlightData?.flight3?.selectedDate, "day");

				if (
					(numberOfMultipleFlightTabletInput === 1 && !isFirstDateValid) ||
					(!useAppState.sovTablet && numberOfMultipleFlightTabletInput > 1 && (!isFirstDateValid || !isFlight1BeforeFlight2)) ||
					(useAppState.sovTablet && numberOfMultipleFlightTabletInput === 2 && !isFlight1BeforeFlight2) ||
					(useAppState.sovTablet && numberOfMultipleFlightTabletInput > 2 && (!isFlight1BeforeFlight2 || !isFlight2BeforeFlight3))
				) {
					return;
				}

				let legs = "";
				const otherLegs = Object.values(_multipleFlightData);

				if (!otherLegs?.length) {
					// eslint-disable-next-line no-console
					console.log("ERROR: Not data to compute search");
					return;
				}

				// Generate all array stored legs queries
				otherLegs.forEach(lg => {
					legs += `&leg[]=${lg?.selectedFrom?.value};${lg?.selectedTo?.value};${dayjs(lg?.selectedDate).format("YYYY-MM-DD")}`;

					trackingArray.push({
						dep_date: dayjs(lg?.selectedDate).format("YYYY-MM-DD"), // YYYY-MM-DD
						dep_month: dayjs(lg?.selectedDate).format("MMMM"),
						from: lg?.selectedFrom?.label, // always proper name
						to: lg?.selectedTo?.label, // always proper name
					});
				});
				
				// Get the last leg data for tracking
				const lastLeg = trackingArray[trackingArray.length - 1];
				let dateDiff = dayjs(lastLeg.dep_date).diff(selectedDates?.[0], "day");
				
				if(useAppState.sovTablet) {
					bookingPathURL = `https://flight.selloffvacations.com/selloff/flights/search?adults=${adults}&children=${
						childrenPlusInfant
					}${childAges}&trip=${selectedType}&preferedClass=${selectedClass.value}&viewtype=R&lang=${
						useAppState.lang
					}${legs}`;
					
					dateDiff = dayjs(lastLeg.dep_date).diff(_multipleFlightData?.flight1?.selectedDate, "day");

				} else {
					bookingPathURL = `https://flight.selloffvacations.com/selloff/flights/search?adults=${adults}&children=${
						childrenPlusInfant
					}${childAges}&trip=${selectedType}&preferedClass=${selectedClass.value}&viewtype=R&lang=${
						useAppState.lang
					}${leg1}${legs}`;
				}

				const commonData = {
					event: "flight_search",
					flight_search_mode: selectedType,
					flight_search_type: selectedClass.value,
					flight_search_num_adult: selectedPassengers.adults,
					flight_search_num_children:
						selectedPassengers.infants + selectedPassengers.children.length,
					flight_search_num_rooms: selectedPassengers.rooms,
					flight_search_total_pax:
						selectedPassengers.adults +
						selectedPassengers.infants +
						selectedPassengers.children.length,
					flight_search_to: lastLeg.to,
					flight_search_duration: dateDiff,
				};

				window.dataLayer.push({
					...commonData,
					flight_search_dep_date: selectedDates?.[0].format("YYYY-MM-DD"),
					flight_search_dep_month: selectedDates?.[0].format("MMMM"),
					flight_search_from: _multipleFlightData?.flight1?.selectedFrom?.label,
				});
			}

			window.open(bookingPathURL, "_blank");
		} else {

			if (!validateSearchCriteria()) {
				return;
			}
			
			const params = {
				engines: "S",
				flex: "Y",
				searchtype: selectedType,
				language: useAppState.lang,
				code_ag: useAppState.codeAg,
				alias: useAppState.alias,
				date_dep: selectedDates[0].format("YYYYMMDD"),
				gateway_dep: selectedFrom.value,
				dest_dep: selectedTo.value,

				nb_adult: selectedPassengers.adults,
				nb_child: selectedPassengers.children.length,
			};

			if (useAppState?.profile) {
				params.sw_staff_profile = useAppState.profile;
			}

			if (selectedType === "RE") {
				params.date_ret = selectedDates[1].format("YYYYMMDD");
			}

			if (selectedPassengers.children.length) {
				for (let index = 0; index < selectedPassengers.children.length; index++) {
					const childAge = selectedPassengers.children[index];

					params[`non_adult${index + 1}`] = childAge;
				}
			}

			await useAppState.SessionManager.setSession({
				flights: {
					selectedType,
					selectedFrom,
					selectedTo,
					selectedDates:
						selectedType === "RE"
							? [selectedDates[0].format(), selectedDates[1].format()]
							: [selectedDates[0].format()],
					selectedPassengers,
				},
			});

			if (openInNewTab) {
				openLinkWithPostMethod(CONFIGS.FLIGHTS.BOOKING_PATH, params, "_blank");
			} else {
				openLinkWithPostMethod(CONFIGS.FLIGHTS.BOOKING_PATH, params);
			}
		}
	};

	/**
	|--------------------------------------------------
	| Handle default from hosting application
	|--------------------------------------------------
	*/
	React.useEffect(() => {
		useAppState.emitter.on("update-flights-defaults", handleUpdateDefaults);

		return () => {
			useAppState.emitter.off("update-flights-defaults");
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useAppState.emitter, handleUpdateDefaults]);

	const flightType =
		selectedType === "oneway" || selectedType === "OW" || selectedType === "multicity"
			? "date"
			: "range";

	if (isSOVBrand) {
		return (
			<form
				ref={referenceElement}
				body-scroll-lock-ignore=""
				className={cx(styles.containerWrapper, {
					[sharedStyles[useAppState.brand]]: sharedStyles[useAppState.brand],
					[styles[useAppState.brand]]: [styles[useAppState.brand]],
					[styles.containerQba]: useAppState.alias === "qba",
					[sharedStyles.containerError]:
						!isValidSelectedTo ||
						!isValidSelectedFrom ||
						!isValidSelectedDates ||
						!isValidSelectedPassengers,
					[sharedStyles.containerErrorStackLayout]: !isValidSelectedTo ||
					(!isValidSelectedFrom ||
					!isValidSelectedDates ||
					!isValidSelectedPassengers) && isStackMobileLayout && useAppState.mode === MODES.SEARCH
				})}
				onSubmit={handleSearch}>
				<PillButtonGroup
					data={dictionary["flight-type"]}
					mode={mode}
					isMobile={isMobile}
					className={cx(styles.pillButtonGroup, {
						[styles.radioButtonGroup]: isMobile,
					})}
					selectedType={selectedType}
					error={!isValidSelectedType}
					onChange={setSelectedType}
				/>
				{/* ***************************** FLIGHT DESKTOP ******************************** */}
				{(useAppState.sovDesktop || (!useAppState.newMobileDesign && isMobile) ||
					(useAppState.sovTablet &&
						(selectedType === "RE" ||
							selectedType === "OW" ||
							selectedType === "oneway" ||
							selectedType === "roundtrip"))) && (
					// eslint-disable-next-line react/jsx-indent
					<div
						className={cx({
							[styles.container]: !ishomePageTabletWidget,
							[styles.SOVMobileStackLayout]: isSOVBrand && !useAppState.sovSimplifiedSearchBar,
							[styles.containerErrorStackLayout]: isStackMobileLayout && useAppState.mode === MODES.SEARCH && (!isValidSelectedFrom || !isValidSelectedTo),
							[styles.containerErrorStackLayoutRoundTrip]: isStackMobileLayout && useAppState.mode === MODES.SEARCH && (!isValidSelectedFrom) && selectedType === "roundtrip",
							[styles.standaloneTab]: ishomePageTabletWidget})}>
						<FromField
							id={FLIGHTS_FROM_FIELD_ID}
							mode={mode}
							className={cx(sharedStyles.field, styles.fromField, {
								[styles.datesFieldMultipleFieldsError]: !isValidSelectedFrom && useAppState.sovTablet
							})}
							classnames={{
								brand: styles[useAppState.brand],
								listViewButton: styles.listViewButton,
								inputIcon: styles.inputIcon,
								listViewIcon: styles.listViewIcon,
							}}
							previousSessionValue={previousSession.selectedFrom}
							selectedType={selectedType}
							selectedFrom={selectedFrom}
							handleSetAvailableDates={setAvailableDates}
							error={!isValidSelectedFrom}
							resetError={() => setIsValidSelectedFrom(true)}
							onChange={setSelectedFrom}
						/>
						<ToField
							id={FLIGHTS_TO_FIELD_ID}
							mode={mode}
							className={cx(sharedStyles.field, styles.toField, {
								[styles.datesFieldMultipleFieldsError]: !isValidSelectedTo && useAppState.sovTablet
							})}
							error={!isValidSelectedTo}
							isSetMultipleFrom={{
									level: 1,
									isSetNextFrom: true,
								}}
							classnames={{
								brand: styles[useAppState.brand],
								listViewButton: styles.listViewButton,
								inputIcon: styles.inputIcon,
								listViewIcon: styles.listViewIcon,
								listViewIconGoingTo: styles.listViewIconGoingTo,
							}}
							selectedTo={selectedTo}
							selectedFrom={selectedFrom}
							selectedType={selectedType}
							multipleFlightData={multipleFlightData}
							previousSessionValue={previousSession.selectedTo}
							resetError={() => setIsValidSelectedTo(true)}
							onChange={setSelectedTo}
							setMultipleFlightData={setMultipleFlightData}
						/>
						<DatesField
							mode={mode}
							className={cx(sharedStyles.field, styles.datesField)}
							classnames={{
								listboxTail: styles.listboxTail,
								listboxTailMultipleFLight: styles.listboxTailMultipleFLight,
							}}
							id={FLIGHTS_DATES_FIELD_ID}
							type={flightType}
							selectedType={selectedType}
							selectedDates={selectedDates}
							availableDates={availableDates}
							previousDates={previousSession.selectedDates}
							error={!isValidSelectedDates}
							ishomePageTabletWidget={ishomePageTabletWidget}
							resetError={() => setIsValidSelectedDates(true)}
							onChange={setSelectedDates}
						/>
						<div className={styles.classPassengerWrap}>
							<ClassField
								id={FLIGHTS_CLASS_ID}
								mode={mode}
								className={cx(sharedStyles.field, styles.classField)}
								selectedClass={selectedClass}
								selectedType={selectedType}
								classList={dictionary["class-list"]}
								onChange={setSelectedClass}
							/>
							<PassengersField
								mode={mode}
								className={cx(sharedStyles.field, styles.passengersField)}
								selectedPassengers={selectedPassengers}
								error={!isValidSelectedPassengers}
								onChange={setSelectedPassengers}
								maxChildAge={maxChildAge}
								searchType="Flights"
								classList={dictionary["class-list"]}
							/>
						</div>
						<Button
							brand={useAppState.brand}
							type="submit"
							sovMobile={!useAppState?.sovSimplifiedSearchBar}
							className={cx(sharedStyles.submitButton, styles.submitButton)}
							label={useAppState.getDictionary("search")}
							onClick={event => {
								handleSearch(event, event.ctrlKey || event.metaKey);
							}}
						/>
					</div>
				)}
				{useAppState.sovDesktop &&
					selectedType === "multicity" &&
					Array(numberOfMultipleFlightTabletInput)
						.fill(0)
						.map((_num, index) => (
							<MultipleFlightComponent
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								index={index}
								selectedType={selectedType}
								numberOfMultipleFlightInput={numberOfMultipleFlightTabletInput}
								styles={{
									brand: styles[useAppState.brand],
									toField: styles.toField,
									fromField: styles.fromField,
									datesField: styles.datesField,
									listViewButton: styles.listViewButton,
									inputIcon: styles.inputIcon,
									listViewIcon: styles.listViewIcon,
									listViewIconGoingTo: styles.listViewIconGoingTo,
									datesFieldMultipleFields: styles.datesFieldMultipleFields,
									toFieldMultipleFields: styles.toFieldMultipleFields,
									fromFieldMultipleFields: styles.fromFieldMultipleFields,
								}}
								classnames={{
									listboxTail: styles.listboxTail,
									listboxTailMultipleFLight: styles.listboxTailMultipleFLight,
								}}
								defaultDate={multipleFlightData?.[`flight${index+1}`]?.selectedDate?.$d ? multipleFlightData?.[`flight${index+1}`]?.selectedDate : selectedDates?.[0]}
								previousDates={previousSession.selectedDates}
								previousSession={previousSession}
								isValidateMultipleCities={isValidateMultipleCities[index]}
								isMultipleFlight={true}
								availableDates={availableDates}
								multipleFlightData={multipleFlightData}
								setMultipleFlightData={setMultipleFlightData}
								setValidateMultipleCities={setValidateMultipleCities}
								setNumberOfMultipleFlightInput={setNumberOfMultipleFlightTabletInput}
							/>
						)
						)}
				{/* ***************************** FLIGHT DESKTOP END ******************************** */}

				{/* ***************************** FLIGHT TABLET ******************************** */}
				{useAppState.sovTablet &&
					selectedType === "multicity" &&
					Array(numberOfMultipleFlightTabletInput)
						.fill(0)
						.map((_num, index) => (
							<MultipleFlightTabletComponent
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								index={index}
								selectedType={selectedType}
								numberOfMultipleFlightInput={numberOfMultipleFlightTabletInput}
								styles={{
									brand: styles[useAppState.brand],
									toField: styles.toField,
									fromField: styles.fromField,
									datesField: styles.datesField,
									listViewButton: styles.listViewButton,
									inputIcon: styles.inputIcon,
									listViewIcon: styles.listViewIcon,
									listViewIconGoingTo: styles.listViewIconGoingTo,
									datesFieldMultipleFields: styles.datesFieldMultipleFields,
									fromFieldTabletMultipleFields: styles.fromFieldTabletMultipleFields,
									toFieldTabletMultipleFields: styles.toFieldTabletMultipleFields,
									fromFieldMultipleFields: styles.fromFieldMultipleFields,
									datesFieldMultipleFieldsError: styles.datesFieldMultipleFieldsError
								}}
								classnames={{
									listboxTail: styles.listboxTail,
									listboxTailMultipleFLight: styles.listboxTailMultipleFLight,
								}}
								defaultDate={multipleFlightData?.[`flight${index+1}`]?.selectedDate?.$d ? multipleFlightData?.[`flight${index+1}`]?.selectedDate : selectedDates?.[0]}
								previousDates={previousSession.selectedDates}
								previousSession={previousSession}
								isValidateMultipleCities={isValidateMultipleCities[index]}
								isMultipleFlight={true}
								availableDates={availableDates}
								multipleFlightData={multipleFlightData}
								setMultipleFlightData={setMultipleFlightData}
								setValidateMultipleCities={setValidateMultipleCities}
								setNumberOfMultipleFlightInput={setNumberOfMultipleFlightTabletInput}
							/>
						))}
				{useAppState.sovTablet && selectedType === "multicity" && (
					<>
						<div className={styles.classAndPassengerFieldTablet}>
							<ClassField
								id={FLIGHTS_CLASS_ID}
								className={cx(sharedStyles.field, styles.classField)}
								selectedClass={selectedClass}
								selectedType={selectedType}
								classList={dictionary["class-list"]}
								error={!isValidSelectedClass}
								onChange={setSelectedClass}
								resetError={() => setIsValidSelectedClass(true)}
							/>
							<PassengersField
								className={cx(sharedStyles.field, styles.passengersField)}
								selectedPassengers={selectedPassengers}
								error={!isValidSelectedPassengers}
								onChange={setSelectedPassengers}
								searchType="Flights"
								maxChildAge={maxChildAge}
								classList={dictionary["class-list"]}
							/>
						</div>
						<Button
							brand={useAppState.brand}
							type="submit"
							className={cx(sharedStyles.submitButton, styles.submitButton)}
							label={useAppState.getDictionary("search")}
							onClick={event => {
								handleSearch(event, event.ctrlKey || event.metaKey);
							}}
						/>
					</>
				)}
				{/* ***************************** FLIGHT TABLET END ******************************** */}
			</form>
		);
	}

	return (
		<form
			className={cx(styles.container, {
				[styles.containerQba]: useAppState.alias === "qba",
			})}
			onSubmit={handleSearch}>
			{useAppState.alias !== "qba" && (
				<TripTypeField
					className={cx(styles.tripTypeField)}
					selectedType={selectedType}
					error={!isValidSelectedType}
					resetError={() => setIsValidSelectedFrom(true)}
					onChange={setSelectedType}
				/>
			)}
			<FromField
				className={cx(sharedStyles.field, styles.fromField)}
				previousSessionValue={previousSession.selectedFrom}
				selectedType={selectedType}
				selectedFrom={selectedFrom}
				handleSetAvailableDates={setAvailableDates}
				error={!isValidSelectedFrom}
				resetError={() => setIsValidSelectedFrom(true)}
				onChange={setSelectedFrom}
			/>
			<ToField
				id={FLIGHTS_TO_FIELD_ID}
				className={cx(sharedStyles.field, styles.toField)}
				previousSessionValue={previousSession.selectedTo}
				selectedType={selectedType}
				selectedFrom={selectedFrom}
				selectedTo={selectedTo}
				error={!isValidSelectedTo}
				resetError={() => setIsValidSelectedTo(true)}
				onChange={setSelectedTo}
			/>
			<DatesField
				className={cx(sharedStyles.field, styles.datesField)}
				id="flights-dates"
				type={selectedType === "OW" ? "date" : "range"}
				selectedDates={selectedDates}
				availableDates={availableDates}
				previousDates={previousSession.selectedDates}
				error={!isValidSelectedDates}
				onChange={setSelectedDates}
			/>
			<PassengersField
				className={cx(sharedStyles.field, styles.passengersField)}
				selectedPassengers={selectedPassengers}
				error={!isValidSelectedPassengers}
				onChange={setSelectedPassengers}
				maxChildAge={maxChildAge}
			/>
			<Button
				type="submit"
				className={cx(sharedStyles.submitButton, styles.submitButton)}
				label={useAppState.getDictionary("search")}
				onClick={event => {
					handleSearch(event, event.ctrlKey || event.metaKey);
				}}
			/>
		</form>
	);
};

Flights.propTypes = {
	tabs: PropTypes.arrayOf(PropTypes.string),
	mode: PropTypes.string,
};

Flights.defaultProps = {
	tabs: undefined,
	mode: MODES.SEARCH
};
export default Flights;
